// ============================================
// src/components/services/ServiceDirectory.js
// Complete Service Directory Component with all subcomponents
// ============================================

// src/components/services/ServiceDirectory.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase.config';
import '../pages/UserDashboard.css';
import './ServiceDirectory.css';

function ServiceDirectory({ userData, onBack }) {
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  const isSuperAdmin = userData?.role === 'superadmin';

  useEffect(() => {
    fetchCategories();
    fetchVendors();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [selectedCategory, darkMode]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      
      if (!error) {
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVendors = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('service_vendors')
        .select('*')
        .eq('is_active', true);

      if (selectedCategory !== 'all') {
        query = query.eq('category_id', selectedCategory);
      }

      if (showEmergencyOnly) {
        query = query.eq('is_emergency', true);
      }

      const { data: vendorsData, error: vendorsError } = await query;
      
      if (vendorsError) {
        console.error('Error fetching vendors:', vendorsError);
        setLoading(false);
        return;
      }

      if (vendorsData && vendorsData.length > 0) {
        const categoryIds = [...new Set(vendorsData.map(v => v.category_id).filter(Boolean))];
        
        const { data: categoriesData } = await supabase
          .from('service_categories')
          .select('id, name, icon')
          .in('id', categoryIds);

        const categoryMap = {};
        if (categoriesData) {
          categoriesData.forEach(cat => {
            categoryMap[cat.id] = cat;
          });
        }

        const vendorIds = vendorsData.map(v => v.id);
        const { data: ratingsData } = await supabase
          .from('vendor_ratings')
          .select('vendor_id, rating')
          .in('vendor_id', vendorIds);

        const ratingsMap = {};
        if (ratingsData) {
          ratingsData.forEach(rating => {
            if (!ratingsMap[rating.vendor_id]) {
              ratingsMap[rating.vendor_id] = [];
            }
            ratingsMap[rating.vendor_id].push(rating.rating);
          });
        }

        const vendorsWithDetails = vendorsData.map(vendor => {
          const ratings = ratingsMap[vendor.id] || [];
          const avgRating = ratings.length > 0 
            ? (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(1)
            : '0.0';
          
          return {
            ...vendor,
            service_categories: categoryMap[vendor.category_id] || { name: 'Unknown', icon: '📋' },
            avgRating,
            totalRatings: ratings.length
          };
        });

        setVendors(vendorsWithDetails);
      } else {
        setVendors([]);
      }
    } catch (error) {
      console.error('Error in fetchVendors:', error);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async (vendorData) => {
    try {
      if (!userData || !userData.id) {
        alert('User information not found. Please refresh and try again.');
        return;
      }

      const insertData = {
        ...vendorData,
        created_by: userData.id,
        is_active: true,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('service_vendors')
        .insert([insertData])
        .select();

      if (error) {
        console.error('Error adding vendor:', error);
        alert('Failed to add vendor: ' + error.message);
      } else {
        setShowAddModal(false);
        alert('Vendor added successfully!');
        await fetchVendors();
      }
    } catch (error) {
      console.error('Error adding vendor:', error);
      alert('Failed to add vendor');
    }
  };

  const handleEditVendor = async (vendorData) => {
    try {
      const { data, error } = await supabase
        .from('service_vendors')
        .update({
          ...vendorData,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingVendor.id)
        .select();

      if (!error) {
        fetchVendors();
        setShowEditModal(false);
        setEditingVendor(null);
        alert('Vendor updated successfully!');
      }
    } catch (error) {
      console.error('Error updating vendor:', error);
      alert('Failed to update vendor');
    }
  };

  const handleDeleteVendor = async (vendor) => {
    if (vendor.created_by !== userData.id && !isSuperAdmin) {
      alert('You can only delete vendors you created!');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${vendor.name}?`)) {
      try {
        const { error } = await supabase
          .from('service_vendors')
          .update({ is_active: false })
          .eq('id', vendor.id);

        if (!error) {
          fetchVendors();
          alert('Vendor deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting vendor:', error);
        alert('Failed to delete vendor');
      }
    }
  };

  const canModifyVendor = (vendor) => {
    return vendor.created_by === userData.id || isSuperAdmin;
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = 
      vendor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.services_offered?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.phone?.includes(searchQuery);
    
    const matchesEmergency = !showEmergencyOnly || vendor.is_emergency;
    
    return matchesSearch && matchesEmergency;
  });

  const stats = {
    total: vendors.length,
    categories: categories.length,
    myVendors: vendors.filter(v => v.created_by === userData.id).length,
    emergency: vendors.filter(v => v.is_emergency).length
  };

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className="menu-item active"
            onClick={() => {
              setSelectedCategory('all');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📞</span>
            <span className="menu-label">All Vendors</span>
          </button>
          
          <button
            className="menu-item"
            onClick={() => {
              setShowAddModal(true);
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">➕</span>
            <span className="menu-label">Add Vendor</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Categories</label>
            <div className="category-filters">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${showEmergencyOnly ? 'active' : ''}`}
            onClick={() => {
              setShowEmergencyOnly(!showEmergencyOnly);
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">🚨</span>
            <span className="menu-label">Emergency Only</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">📞 Service Directory</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="service-header">
            <h2 className="page-title">📞 Service Directory</h2>
            <div className="header-actions">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="btn-secondary"
              >
                {viewMode === 'grid' ? '📋 List' : '⚏ Grid'}
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary"
              >
                + Add Vendor
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>📊 Total</h3>
                <p className="stat-value">{stats.total}</p>
                <small>Vendors</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-green">
              <div className="stat-content">
                <h3>📁 Categories</h3>
                <p className="stat-value">{stats.categories}</p>
                <small>Services</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>👤 My Vendors</h3>
                <p className="stat-value">{stats.myVendors}</p>
                <small>Added by You</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-red">
              <div className="stat-content">
                <h3>🚨 Emergency</h3>
                <p className="stat-value">{stats.emergency}</p>
                <small>24/7 Available</small>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="dashboard-card search-card">
            <input
              type="text"
              placeholder="🔍 Search vendors by name, service, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Vendors Display */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading vendors...</p>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="dashboard-card empty-state">
              <div className="empty-icon">📭</div>
              <p>No vendors found</p>
              <small>Add your first vendor or adjust filters</small>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="vendors-grid">
              {filteredVendors.map(vendor => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onEdit={() => {
                    setEditingVendor(vendor);
                    setShowEditModal(true);
                  }}
                  onDelete={() => handleDeleteVendor(vendor)}
                  canModify={canModifyVendor(vendor)}
                  userData={userData}
                />
              ))}
            </div>
          ) : (
            <VendorListView
              vendors={filteredVendors}
              onEdit={(vendor) => {
                setEditingVendor(vendor);
                setShowEditModal(true);
              }}
              onDelete={handleDeleteVendor}
              canModifyVendor={canModifyVendor}
              userData={userData}
            />
          )}
        </div>
      </main>

      {/* Modals */}
      {(showAddModal || showEditModal) && (
        <VendorModal
          vendor={editingVendor}
          categories={categories}
          onSave={showEditModal ? handleEditVendor : handleAddVendor}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setEditingVendor(null);
          }}
        />
      )}
    </div>
  );
}

// Vendor Card Component
function VendorCard({ vendor, onEdit, onDelete, canModify, userData }) {
  const isOwner = vendor.created_by === userData.id;
  
  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      padding: '20px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      position: 'relative'
    }}>
      {isOwner && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: '#28a745',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '10px'
        }}>
          YOUR LISTING
        </div>
      )}

      <h3 style={{ marginTop: 0 }}>{vendor.name}</h3>
      <p style={{ color: '#666', fontSize: '14px' }}>
        {vendor.service_categories?.icon} {vendor.service_categories?.name}
      </p>
      
      <div style={{ marginBottom: '10px' }}>
        <div>📱 {vendor.phone}</div>
        {vendor.email && <div>📧 {vendor.email}</div>}
        {vendor.address && <div>📍 {vendor.address}</div>}
      </div>

      {vendor.services_offered && (
        <p style={{ fontSize: '14px', color: '#666' }}>
          {vendor.services_offered}
        </p>
      )}

      {vendor.price_range && (
        <p style={{ fontSize: '14px' }}>💰 {vendor.price_range}</p>
      )}

      <div style={{ 
        display: 'flex', 
        gap: '10px',
        marginTop: '15px'
      }}>
        <button
          onClick={() => window.location.href = `tel:${vendor.phone}`}
          style={{
            flex: 1,
            padding: '8px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          📞 Call
        </button>
        
        {canModify && (
          <>
            <button
              onClick={onEdit}
              style={{
                flex: 1,
                padding: '8px',
                background: '#ffc107',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={onDelete}
              style={{
                flex: 1,
                padding: '8px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              🗑️
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// List View Component
function VendorListView({ vendors, onEdit, onDelete, canModifyVendor, userData }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8f9fa' }}>
            <th style={{ padding: '15px', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '15px', textAlign: 'left' }}>Category</th>
            <th style={{ padding: '15px', textAlign: 'left' }}>Phone</th>
            <th style={{ padding: '15px', textAlign: 'left' }}>Services</th>
            <th style={{ padding: '15px', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map(vendor => (
            <tr key={vendor.id} style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ padding: '15px' }}>
                {vendor.name}
                {vendor.created_by === userData.id && (
                  <span style={{
                    marginLeft: '10px',
                    fontSize: '10px',
                    background: '#28a745',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px'
                  }}>
                    YOURS
                  </span>
                )}
              </td>
              <td style={{ padding: '15px' }}>
                {vendor.service_categories?.icon} {vendor.service_categories?.name}
              </td>
              <td style={{ padding: '15px' }}>{vendor.phone}</td>
              <td style={{ padding: '15px' }}>{vendor.services_offered || '-'}</td>
              <td style={{ padding: '15px' }}>
                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                  <button
                    onClick={() => window.location.href = `tel:${vendor.phone}`}
                    style={{
                      padding: '5px 10px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Call
                  </button>
                  {canModifyVendor(vendor) && (
                    <>
                      <button
                        onClick={() => onEdit(vendor)}
                        style={{
                          padding: '5px 10px',
                          background: '#ffc107',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(vendor)}
                        style={{
                          padding: '5px 10px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Vendor Modal Component
function VendorModal({ vendor, categories, onSave, onClose }) {
  const [formData, setFormData] = useState({
    category_id: vendor?.category_id || '',
    name: vendor?.name || '',
    phone: vendor?.phone || '',
    alternate_phone: vendor?.alternate_phone || '',
    email: vendor?.email || '',
    address: vendor?.address || '',
    services_offered: vendor?.services_offered || '',
    price_range: vendor?.price_range || '',
    availability: vendor?.availability || '',
    is_emergency: vendor?.is_emergency || false
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.category_id) newErrors.category_id = 'Category is required';
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (!formData.phone?.trim()) newErrors.phone = 'Phone is required';
    
    // Phone validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/[- ]/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (formData.alternate_phone && !phoneRegex.test(formData.alternate_phone.replace(/[- ]/g, ''))) {
      newErrors.alternate_phone = 'Please enter a valid 10-digit phone number';
    }
    
    // Email validation
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }
    
    // Price range validation
    if (formData.price_range) {
      const priceRegex = /^₹?\d+(-\d+)?$/;
      if (!priceRegex.test(formData.price_range.replace(/\s/g, ''))) {
        newErrors.price_range = 'Please enter valid price format (e.g., ₹500-2000)';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Clean phone numbers
    const cleanedData = {
      ...formData,
      phone: formData.phone.replace(/[- ]/g, ''),
      alternate_phone: formData.alternate_phone?.replace(/[- ]/g, '') || null,
      name: formData.name.trim(),
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      services_offered: formData.services_offered?.trim() || null,
      price_range: formData.price_range?.trim() || null,
      availability: formData.availability?.trim() || null
    };
    
    onSave(cleanedData);
  };

  return (
    <div className="vendor-modal-overlay">
      <div className="vendor-modal">
        <h3>{vendor ? 'Edit Vendor' : 'Add New Vendor'}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Category *</label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              className={`form-select ${errors.category_id ? 'error' : ''}`}
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
            {errors.category_id && <span className="error-text">{errors.category_id}</span>}
          </div>

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className={`form-input ${errors.name ? 'error' : ''}`}
              maxLength="100"
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className={`form-input ${errors.phone ? 'error' : ''}`}
              placeholder="10 digit number"
              maxLength="10"
            />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label>Alternate Phone</label>
            <input
              type="tel"
              value={formData.alternate_phone}
              onChange={(e) => setFormData({...formData, alternate_phone: e.target.value})}
              className={`form-input ${errors.alternate_phone ? 'error' : ''}`}
              placeholder="10 digit number"
              maxLength="10"
            />
            {errors.alternate_phone && <span className="error-text">{errors.alternate_phone}</span>}
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className={`form-input ${errors.email ? 'error' : ''}`}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="form-textarea"
              rows="2"
              maxLength="500"
            />
          </div>

          <div className="form-group">
            <label>Services Offered</label>
            <textarea
              value={formData.services_offered}
              onChange={(e) => setFormData({...formData, services_offered: e.target.value})}
              className="form-textarea"
              rows="3"
              maxLength="500"
            />
          </div>

          <div className="form-group">
            <label>Price Range</label>
            <input
              type="text"
              value={formData.price_range}
              onChange={(e) => setFormData({...formData, price_range: e.target.value})}
              placeholder="e.g., ₹500-2000"
              className={`form-input ${errors.price_range ? 'error' : ''}`}
            />
            {errors.price_range && <span className="error-text">{errors.price_range}</span>}
          </div>

          <div className="form-group">
            <label>Availability</label>
            <input
              type="text"
              value={formData.availability}
              onChange={(e) => setFormData({...formData, availability: e.target.value})}
              placeholder="e.g., Mon-Sat, 9AM-6PM"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.is_emergency}
                onChange={(e) => setFormData({...formData, is_emergency: e.target.checked})}
                style={{ width: 'auto' }}
              />
              <span>🚨 Mark as Emergency Service (24/7 Available)</span>
            </label>
          </div>

          <div className="modal-actions">
            <button
              type="submit"
              className="btn-primary"
            >
              {vendor ? 'Update' : 'Add'} Vendor
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ServiceDirectory;